import { useMemo } from 'react'
import { notifications } from '@mantine/notifications'
import { useQueryClient } from '@tanstack/react-query'

import { BudgetForm } from '@/components/BudgetForm/BudgetForm'
import type { BudgetFormSchema } from '@/components/BudgetForm/BudgetForm.types'
import { budgetFormDefaultValues } from '@/components/BudgetForm/BudgetForm.values'
import type { BudgetFormEditProps } from '@/components/BudgetFormEdit/BudgetFormEdit.types'

import {
  useBudgetServiceBudgetControllerGetAllBudgetsKey,
  useBudgetServiceBudgetControllerUpdateBudget,
} from '@/__generated__/queries'

import { INSIGHTS_QUERY_KEY } from '@/hooks/useInsightsQuery.hook'

const INVALIDATE_KEYS = [
  INSIGHTS_QUERY_KEY,
  useBudgetServiceBudgetControllerGetAllBudgetsKey,
]

export function BudgetFormEdit({
  budget,
  budgetId,
  currentBudget,
  onClose,
}: BudgetFormEditProps) {
  const queryClient = useQueryClient()

  const initialValues = useMemo<BudgetFormSchema | undefined>(() => {
    if (!budget) return undefined

    return budgetFormDefaultValues(budget)
  }, [budget])

  const updateBudgetMutation = useBudgetServiceBudgetControllerUpdateBudget({
    onSuccess: () => {
      notifications.show({
        color: 'green',
        message: 'Budget updated successfully',
        title: 'Budget updated',
      })

      INVALIDATE_KEYS.forEach(key => {
        queryClient.invalidateQueries({
          queryKey: [key],
        })
      })

      onClose()
    },
  })

  const handleSubmit = (values: BudgetFormSchema) =>
    updateBudgetMutation.mutate({
      budgetId: +budgetId!,
      requestBody: values,
    })

  return (
    <BudgetForm
      currentBudget={currentBudget}
      data={initialValues}
      edit
      onClose={onClose}
      onSubmit={handleSubmit}
    />
  )
}
